import { useEffect, useState } from "react"

// ─── Config ─────────────────────────────────────────────────────────────────

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ||
  "https://startups.cloudflare.app"

// ─── Types ──────────────────────────────────────────────────────────────────

type EventInfo = {
  name: string
  welcomeHeading?: string
  creditTier: number
}

type FormData = {
  name: string
  email: string
  accountId: string
  hasPaymentMethod: boolean
}

const emptyForm: FormData = {
  name: "",
  email: "",
  accountId: "",
  hasPaymentMethod: false,
}

type Status =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "ready"; event: EventInfo }
  | { kind: "submitting"; event: EventInfo }
  | { kind: "success"; event: EventInfo }
  | { kind: "error"; event: EventInfo; message: string }

// ─── Helpers ────────────────────────────────────────────────────────────────

function getSlugFromPath(): string | null {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, "")
  if (!path) return null
  return path.split("/")[0].toLowerCase()
}

function getTokenFromQuery(): string | null {
  const params = new URLSearchParams(window.location.search)
  const t = params.get("t")
  return t && t.length > 0 ? t : null
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString()}`
}

function getWelcomeHeading(event: EventInfo): string {
  return event.welcomeHeading || `Thank you for attending ${event.name}!`
}

// ─── App ────────────────────────────────────────────────────────────────────

export function App() {
  const slug = getSlugFromPath()
  const token = getTokenFromQuery()
  const [status, setStatus] = useState<Status>({ kind: "loading" })
  const [form, setForm] = useState<FormData>(emptyForm)

  useEffect(() => {
    if (!slug || !token) {
      setStatus({ kind: "invalid" })
      return
    }

    let cancelled = false
    const url = `${API_BASE}/api/v1/events/${encodeURIComponent(slug)}?t=${encodeURIComponent(token)}`

    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status))
        return res.json() as Promise<EventInfo>
      })
      .then((event) => {
        if (!cancelled) setStatus({ kind: "ready", event })
      })
      .catch(() => {
        if (!cancelled) setStatus({ kind: "invalid" })
      })

    return () => {
      cancelled = true
    }
  }, [slug, token])

  async function handleSubmit(event: EventInfo) {
    if (!slug || !token) return
    setStatus({ kind: "submitting", event })

    const name = form.name.trim()
    const email = form.email.trim()
    const accountId = form.accountId.trim()
    const payload = {
      name,
      email,
      accountId,
      companyName: name,
      website: "https://www.cloudflare.com/startups/",
      foundedYear: new Date().getFullYear(),
      contactName: name,
      contactEmail: email,
      cloudflareAccountId: accountId,
      cloudflareAccountEmail: email,
    }

    try {
      const url = `${API_BASE}/api/v1/events/${encodeURIComponent(slug)}/redeem?t=${encodeURIComponent(token)}`
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const errBody = (await res.json().catch(() => null)) as
          | { error?: string; errors?: { field: string; message: string }[] }
          | null
        const message =
          errBody?.errors?.[0]?.message ??
          errBody?.error ??
          `Submission failed (${res.status})`
        setStatus({ kind: "error", event, message })
        return
      }

      setStatus({ kind: "success", event })
    } catch (err) {
      setStatus({
        kind: "error",
        event,
        message:
          err instanceof Error ? err.message : "Network error. Please try again.",
      })
    }
  }

  const activeEvent =
    status.kind === "ready" ||
    status.kind === "submitting" ||
    status.kind === "error"
      ? status.event
      : null

  return (
    <div className="flex min-h-svh flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">
        <CloudflareLogo />

        {status.kind === "loading" && (
          <Card>
            <p className="text-center text-sm text-neutral-500">Loading...</p>
          </Card>
        )}

        {status.kind === "invalid" && (
          <Card>
            <h1 className="mb-2 text-xl font-semibold">
              This link is no longer active
            </h1>
            <p className="text-sm text-neutral-600">
              The redemption link you used has expired, reached its limit, or
              is not valid. If you would still like to apply for Cloudflare for
              Startups, you can apply directly at{" "}
              <a
                href="https://www.cloudflare.com/startups/"
                className="text-brand hover:underline"
              >
                cloudflare.com/startups
              </a>
              .
            </p>
          </Card>
        )}

        {activeEvent && (
          <Card>
            <h1 className="mb-1 text-xl font-semibold">
              {getWelcomeHeading(activeEvent)}
            </h1>
            <p className="mb-5 text-sm text-neutral-600">
              Claim your{" "}
              <span className="font-semibold text-neutral-900">
                {formatCurrency(activeEvent.creditTier)} in Cloudflare credits
              </span>
              .
            </p>

            <RedemptionForm
              form={form}
              setForm={setForm}
              submitting={status.kind === "submitting"}
              error={status.kind === "error" ? status.message : null}
              onSubmit={() => handleSubmit(activeEvent)}
            />
          </Card>
        )}

        {status.kind === "success" && (
          <Card>
            <h1 className="mb-2 text-xl font-semibold">
              Thanks, we got your submission.
            </h1>
            <p className="mb-4 text-sm text-neutral-600">
              Our team will use the Cloudflare account details you provided to
              apply your event credits. This usually takes a few business days.
            </p>
            <p className="text-sm text-neutral-600">
              Questions? Email us at{" "}
              <a
                href="mailto:startups@cloudflare.com"
                className="text-brand hover:underline"
              >
                startups@cloudflare.com
              </a>
              .
            </p>
          </Card>
        )}

        <p className="mt-6 text-center text-xs text-neutral-400">
          Cloudflare for Startups
        </p>
      </div>
    </div>
  )
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
      {children}
    </div>
  )
}

function CloudflareLogo() {
  return (
    <div className="mb-6 flex items-center justify-center gap-2">
      <img
        src="/logo.png"
        className="h-7 w-auto"
        aria-hidden="true"
        alt=""
      />
      <span className="text-sm font-medium text-neutral-700">
        Cloudflare for Startups
      </span>
    </div>
  )
}

function RedemptionForm({
  form,
  setForm,
  submitting,
  error,
  onSubmit,
}: {
  form: FormData
  setForm: React.Dispatch<React.SetStateAction<FormData>>
  submitting: boolean
  error: string | null
  onSubmit: () => void
}) {
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    onSubmit()
  }

  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.accountId.trim() &&
    form.hasPaymentMethod

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <FormField label="Name">
        <input
          type="text"
          required
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="form-input"
          placeholder="Jane Founder"
          autoComplete="name"
        />
      </FormField>

      <FormField
        label="Cloudflare account email"
        hint="The email you use to log in at dash.cloudflare.com."
      >
        <input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className="form-input"
          placeholder="jane@acme.com"
          autoComplete="email"
        />
      </FormField>

      <FormField
        label="Cloudflare account ID"
        hint="Required. Find this in your Cloudflare dashboard account URL or account overview."
      >
        <input
          type="text"
          required
          value={form.accountId}
          onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}
          className="form-input font-mono text-xs"
          placeholder="32-character account tag"
          autoComplete="off"
        />
      </FormField>

      <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        Need help finding your account ID? Follow Cloudflare's guide to{" "}
        <a
          href="https://developers.cloudflare.com/fundamentals/account/find-account-and-zone-ids/"
          className="font-medium underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          find account and zone IDs
        </a>
        .
      </div>

      <label className="flex gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
        <input
          type="checkbox"
          required
          checked={form.hasPaymentMethod}
          onChange={(e) =>
            setForm((f) => ({ ...f, hasPaymentMethod: e.target.checked }))
          }
          className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-brand focus:ring-brand/20"
        />
        <span>
          I confirm this Cloudflare account has a payment method on file, which
          is required to distribute credits.
        </span>
      </label>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!canSubmit || submitting}
        className="mt-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? "Submitting..." : "Claim credits"}
      </button>

      <p className="text-center text-[11px] text-neutral-400">
        By submitting, you agree to our{" "}
        <a
          href="https://www.cloudflare.com/terms/"
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms
        </a>{" "}
        and{" "}
        <a
          href="https://www.cloudflare.com/privacypolicy/"
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
        .
      </p>
    </form>
  )
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-neutral-700">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-neutral-500">{hint}</span>}
    </label>
  )
}
